import React from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { SMLButton } from '../../common/views/SMLButton/SMLButton';
import { MeiliTrackData } from '../../music24/components/SearchField/types';
import { SongSearch } from './useSongSearch';
import { SongResultRow } from './SongResultRow';
import { addSongMessages } from './addSongMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole page; each part uses its own slice of it */
import styles from './AddSongView.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface SongSearchResultsProps {
  query: string;
  artistName: string;
  search: SongSearch;
  pickedId: string | undefined;
  disabled: boolean;
  onPick: (id: string) => void;
  onAddUnreleased: () => void;
}

function subtitleOf(track: MeiliTrackData): string {
  return [track.artistName, track.albumName].filter((part) => part !== undefined && part !== '').join(' · ');
}

/** The search answer of "It is out" (design c1, c3 "No match"): the artist's songs first. */
const SongSearchResultsComponent: React.FC<SongSearchResultsProps> = ({
  query,
  artistName,
  search,
  pickedId,
  disabled,
  onPick,
  onAddUnreleased,
}) => {
  const intl = useIntl();

  if (search.isSearching && search.own.length === 0 && search.others.length === 0) {
    return <p className={styles.tiny}>{intl.formatMessage(addSongMessages.searching)}</p>;
  }
  if (search.isFailed) {
    return (
      <div className={styles.noMatch} role="alert" data-testid="song-search-failed">
        <b>{intl.formatMessage(addSongMessages.errorSearchFailed)}</b>
        <p>{intl.formatMessage(addSongMessages.errorSearchFailedBody)}</p>
        {/* One button on its own line (QA 2026-10-28, mobr2-4): the links ran together, and
            "Type it in instead" is under the search already. */}
        <div>
          <SMLButton outline omitCap size="sm" onClick={search.retry} testID="song-search-retry">
            {intl.formatMessage(addSongMessages.errorSearchRetry)}
          </SMLButton>
        </div>
      </div>
    );
  }
  if (search.isEmpty) {
    return (
      <div className={styles.noMatch} role="status">
        <b>{intl.formatMessage(addSongMessages.errorNoMatch, { query: query.trim() })}</b>
        <p>
          {intl.formatMessage(addSongMessages.errorNoMatchBody)}{' '}
          <button type="button" className={styles.linkButton} onClick={onAddUnreleased}>
            {intl.formatMessage(addSongMessages.errorNoMatchUnreleased)}
          </button>
        </p>
      </div>
    );
  }

  const row = (track: MeiliTrackData) => (
    <SongResultRow
      key={track.id}
      id={track.id}
      title={track.name}
      subtitle={subtitleOf(track)}
      imageUrl={track.imageUrl}
      isPicked={track.id === pickedId}
      disabled={disabled}
      onPick={onPick}
    />
  );

  return (
    <div className={styles.results}>
      {search.own.length > 0 && (
        <>
          <div className={styles.resultsGroup}>
            {intl.formatMessage(addSongMessages.searchGroup, { artist: artistName })}
          </div>
          {search.own.map(row)}
        </>
      )}
      {search.others.map(row)}
    </div>
  );
};

export const SongSearchResults = TypedMemo(SongSearchResultsComponent);
